import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import Places from 'constants/places'
import { DEFAULT_SEARCH_SETTINGS } from 'constants/searchHelpers'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import isHotelFlightBundleOffer from 'lib/offer/isHotelFlightBundleOffer'
import { isHotelPackage, isLEHotel } from 'lib/offer/offerTypes'
import { hasDisplayableOfferPrice } from 'lib/offer/pricingVisibility'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import { getHotelsTrendingDestinations } from 'selectors/destinationSelectors'
import { getAllOffers } from 'selectors/offerSelectors'
import LoyaltySpendPointsCarouselSection from './LoyaltySpendPointsCarouselSection'
import LoyaltySpendPointsOfferFilterPrefetch from './LoyaltySpendPointsOfferFilterPrefetch'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { DESTINATION_AVAILABILITY_PROBE_LIMIT, MAX_CAROUSEL_FILTER_OPTIONS, MINIMUM_OFFERS_TO_SHOW_CAROUSEL, SPEND_POINTS_CAROUSEL_RENDER_LIMIT } from './loyaltySpendPointsConstants'
import useLoyaltySpendPointsFilterAvailability from './useLoyaltySpendPointsFilterAvailability'

const ALL_DESTINATIONS_VALUE = ''
/** Flash + LPC only — bedbank/villa anytime burn is unreliable without live rates/mx. */
const SPEND_POINTS_HOTEL_OFFER_TYPES: Array<App.OfferType> = [OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON]
const SORT_BY_OPTION: Record<SpendPointsSortOption, App.OfferListSortByOptions> = {
  recommended: 'recommended',
  'lowest-points': 'points.asc',
  'highest-points': 'points.desc',
}

function isSpendPointsHotelEligible(
  offer: App.AnyOffer | undefined,
): offer is App.HotelOffer | App.HotelOfferSummary {
  if (!offer || !isLEHotel(offer)) return false
  if (isHotelFlightBundleOffer(offer)) return false
  // Walled (logged-out) offers are let through — the card itself renders the sign-up wall.
  return hasDisplayableOfferPrice(offer)
}

interface Props {
  title: ReactNode
  sortOption: SpendPointsSortOption
}

function LoyaltySpendPointsHotelCarousel(props: Props) {
  const { title, sortOption } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const trendingDestinations = useAppSelector(getHotelsTrendingDestinations)
  const allOffers = useAppSelector(getAllOffers)
  const offerErrors = useAppSelector(state => state.offer.offerErrors)
  const [selectedDestination, setSelectedDestination] = useState(ALL_DESTINATIONS_VALUE)

  useEffect(() => {
    dispatch(fetchTrendingDestinations())
  }, [dispatch])

  const destinationOptions = useMemo(() => [
    {
      value: ALL_DESTINATIONS_VALUE,
      label: intl.formatMessage({
        id: 'pZdaV3-F6vv3YpNkMRU5',
        defaultMessage: 'Around the world',
        description: 'Filter option label for hotel offers available around the world on the Societe spend points page',
      }),
    },
    ...trendingDestinations
      .filter(destination => destination.value !== Places.Anywhere.id)
      .slice(0, MAX_CAROUSEL_FILTER_OPTIONS - 1)
      .map(destination => ({
        value: destination.value,
        label: destination.format.mainText,
      })),
  ], [intl, trendingDestinations])

  const onDestinationChange = useCallback((destination: string) => {
    setSelectedDestination(currentDestination => (
      currentDestination === destination ? ALL_DESTINATIONS_VALUE : destination
    ))
  }, [])

  const sortBy = SORT_BY_OPTION[sortOption]
  const balancePointsLte = showOffersWithinBalance && burnPointsLimit !== undefined
    ? burnPointsLimit
    : undefined

  // destinationId stays out of this base so chip prefetch list keys stay stable across chip changes.
  const baseOfferListFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: SPEND_POINTS_HOTEL_OFFER_TYPES,
    limit: SPEND_POINTS_CAROUSEL_RENDER_LIMIT,
    sortBy,
    // points.*/pointsLte require the search socket (see streamRequest); light search has no points support.
    isStream: true,
    viewPriceInPoints: true,
    rooms: [DEFAULT_SEARCH_SETTINGS.defaultOccupants],
    // Best-effort only — isSpendPointsHotelEligible above is the real filter.
    excludeCharterOffers: true,
    pointsLte: balancePointsLte,
  }), [balancePointsLte, sortBy])

  const offerListFilters = useMemo<App.OfferListFilters>(() => ({
    ...baseOfferListFilters,
    destinationId: selectedDestination || Places.Anywhere.id,
  }), [baseOfferListFilters, selectedDestination])

  const offerList = useOfferList(offerListFilters, {
    // Flash/LPC anytime prices already come from offer summaries; don't wait on per-offer hydration.
    filterUnpriced: false,
    // Matches the Search/Société points integration doc's minOffersRequired convention.
    minRequiredOffers: offerListFilters.limit,
  })
  const [, offersFetching] = useOffers(offerList.offerIds)

  // offerList.fetching flips false on the first streamed batch, well before the socket finishes —
  // require streamEnded too so the carousel doesn't render/reflow while more IDs are still arriving.
  const areSearchOffersResolved = !offerList.fetching
    && !!offerList.streamEnded
    && !offersFetching
    && offerList.offerIds.every(id => !!allOffers[id] || !!offerErrors[id])

  // Trust the server's pointsLte filter and points sort as a starting point — no need to
  // re-derive a cash cap client-side. The sort itself is member-rate-blind though, so it's
  // corrected below (see isPointsSort).
  const displayOfferList = useMemo<App.OfferList>(() => {
    if (!areSearchOffersResolved) {
      return {
        ...offerList,
        fetching: true,
        offerIds: [],
      }
    }

    return {
      ...offerList,
      offerIds: offerList.offerIds.filter(id => isSpendPointsHotelEligible(allOffers[id])),
    }
  }, [allOffers, areSearchOffersResolved, offerList])

  // svc-search's points sort uses the non-member rate, so member-priced offers can land out of
  // order — re-sort client-side using the same live burn price each card displays.
  const isPointsSort = sortOption !== 'recommended'
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const burnCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations)

  const pointsSortCandidates = useMemo(() => {
    if (!isPointsSort) return []
    return displayOfferList.offerIds.flatMap(id => {
      const offer = allOffers[id] as App.HotelOffer | App.HotelOfferSummary | undefined
      const pkg = offer?.lowestPricePackage
      const loyaltyProductType = offer && getLoyaltyProductType(offer)
      if (!offer || !pkg?.price || !loyaltyProductType) return []

      const propertyFees = pkg.propertyFees ?? 0
      const price = pkg.price + propertyFees
      const memberPrice = pkg.memberPrice > 0 ? pkg.memberPrice + propertyFees : undefined
      const mx = getHotelEncryptedMargin(offer.type, isHotelPackage(pkg) ? pkg : undefined)

      return [{
        id,
        standard: { price, loyaltyProductType, mx },
        member: memberPrice ? { price: memberPrice, loyaltyProductType, mx } : undefined,
      }]
    })
  }, [isPointsSort, displayOfferList.offerIds, allOffers])

  useEffect(() => {
    for (const { standard, member } of pointsSortCandidates) {
      dispatch(submitLoyaltyPointsBurnCalculationRequest(standard, { viewPriceInPoints: true }))
      if (member) {
        dispatch(submitLoyaltyPointsBurnCalculationRequest(member, { viewPriceInPoints: true }))
      }
    }
  }, [pointsSortCandidates, dispatch])

  const pointsSortResolved = pointsSortCandidates.every(({ standard }) => {
    const calculation = burnCalculations?.[buildBurnKey(standard, customerId)]
    return calculation?.data !== undefined || calculation?.error !== undefined
  })

  const finalOfferList = useMemo<App.OfferList>(() => {
    if (!isPointsSort || displayOfferList.fetching) return displayOfferList
    if (!pointsSortResolved) {
      return { ...displayOfferList, fetching: true, offerIds: [] }
    }

    const pointsByOfferId = new Map<string, number>()
    for (const { id, standard, member } of pointsSortCandidates) {
      const standardPoints = burnCalculations?.[buildBurnKey(standard, customerId)]?.data?.priceInPoints
      const memberPoints = member ? burnCalculations?.[buildBurnKey(member, customerId)]?.data?.priceInPoints : undefined
      const points = memberPoints ?? standardPoints
      if (points !== undefined) pointsByOfferId.set(id, points)
    }

    const direction = sortOption === 'lowest-points' ? 1 : -1
    // Offers whose burn calc errored get appended unsorted rather than dropped.
    const sortedOfferIds = displayOfferList.offerIds
      .filter(id => pointsByOfferId.has(id))
      .sort((a, b) => (pointsByOfferId.get(a)! - pointsByOfferId.get(b)!) * direction)
    const unresolvedOfferIds = displayOfferList.offerIds.filter(id => !pointsByOfferId.has(id))

    return { ...displayOfferList, offerIds: [...sortedOfferIds, ...unresolvedOfferIds] }
  }, [isPointsSort, displayOfferList, pointsSortResolved, pointsSortCandidates, burnCalculations, customerId, sortOption])

  useEffect(() => {
    if (
      selectedDestination !== ALL_DESTINATIONS_VALUE &&
      !finalOfferList.fetching &&
      finalOfferList.offerIds.length === 0
    ) {
      setSelectedDestination(ALL_DESTINATIONS_VALUE)
    }
  }, [selectedDestination, finalOfferList.fetching, finalOfferList.offerIds.length])

  // Wait for a known points cap before enabling chip prefetch when the balance filter is on.
  const filterAvailabilityKey = showOffersWithinBalance
    ? (burnPointsLimit !== undefined ? String(burnPointsLimit) : undefined)
    : 'all'
  const isFilterAvailabilityEnabled = filterAvailabilityKey !== undefined

  const { visibleOptions: visibleDestinationOptions, onAvailabilityChange: onDestinationAvailabilityChange, isResolved: areDestinationFiltersResolved, shouldShowFilters } = useLoyaltySpendPointsFilterAvailability({
    options: destinationOptions,
    selectedValue: selectedDestination,
    defaultValue: ALL_DESTINATIONS_VALUE,
    enabled: isFilterAvailabilityEnabled,
    availabilityKey: filterAvailabilityKey,
    onSelectedValueChange: setSelectedDestination,
  })

  // Pin sortBy so switching the carousel's sort never re-queries/reflows these chip probes.
  const destinationPrefetchFilters = useMemo(() => (
    destinationOptions
      .filter(option => !!option.value)
      .map(option => ({
        value: option.value,
        filters: {
          ...baseOfferListFilters,
          destinationId: option.value,
          limit: DESTINATION_AVAILABILITY_PROBE_LIMIT,
          sortBy: 'recommended' as const,
        },
      }))
  ), [baseOfferListFilters, destinationOptions])

  if (!selectedDestination && !finalOfferList.fetching && finalOfferList.offerIds.length < MINIMUM_OFFERS_TO_SHOW_CAROUSEL) {
    return null
  }

  return <>
    {isFilterAvailabilityEnabled && destinationPrefetchFilters.map(({ value, filters }) => <LoyaltySpendPointsOfferFilterPrefetch
      key={`${value}:${filterAvailabilityKey}`}
      value={value}
      filters={filters}
      onAvailabilityChange={onDestinationAvailabilityChange}
    />)}
    <LoyaltySpendPointsCarouselSection
      title={title}
      viewAllTo="/hotels"
      filters={offerListFilters}
      offerList={finalOfferList}
      filterOptions={visibleDestinationOptions}
      selectedFilter={selectedDestination}
      onFilterChange={onDestinationChange}
      showFilters={shouldShowFilters}
      filtersLoading={isFilterAvailabilityEnabled && shouldShowFilters && !areDestinationFiltersResolved}
      testId="loyalty-spend-points-hotels-carousel"
    />
  </>
}

export default LoyaltySpendPointsHotelCarousel
